import { useEffect, useRef, useState } from 'react';

import {
  getPlaceholderHtml as getSelectPlaceholderHtml,
  SelectPlaceholder,
  tag as selectPlaceholderTag,
} from '@components/field-mapping-select-field/extensions';
import { SelectOption } from '@components/select';
import { showExtendedSidebar, useExtendedSidebarData } from '@components/sidebar';
import {
  BaseFieldMappingFieldProps,
  ExtendedFieldMappingEditorValue,
  FieldMappingData,
  FieldMappingEditorValue,
  FieldMappingFieldItem,
  transformInput,
  transformSimpleValueInput,
  transformSimpleValueOutput,
} from '@components/text-editors/addons/field-mapping';
import { SingleVariableOrChips } from '@components/text-editors/addons/single-variable-or-chips';
import { FieldMappingSidebar } from '@components/text-editors/field-mapping-sidebar';
import { NodeStyledFieldMappingTextEditor } from '@components/text-editors/field-mapping-text-editor';
import { EditorMethods } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';
import { uuid } from '@utils/uuid';

export type FieldMappingSelectMultiValue = SelectOption[] | FieldMappingEditorValue;

export type FieldMappingSelectMultiProps = Omit<
  BaseFieldMappingFieldProps<FieldMappingSelectMultiValue>,
  'defaultValue'
> & {
  options: SelectOption[];
  defaultValue?: FieldMappingSelectMultiValue;
} & FieldMappingData;

/**
 * Older condition values were stored as plain strings, or as a single
 * {value, label} dict for select_any_of -- the shape override_option_value
 * handles server-side. Both have to reach the chip path: a string has no
 * `.value` to read, and a lone dict used to fall through to the editor-value
 * branch and render as plain text. Either way the selection was silently
 * dropped on the next edit, because parseEditorOutput found no placeholders.
 */
const toSelectedValue = (entry: SelectOption | string): string =>
  typeof entry === 'string' ? entry : entry.value;

const isLegacySingleOption = (value: FieldMappingSelectMultiValue): value is SelectOption =>
  'label' in value;

// Not in the declared union, but legacy stored values can still be strings.
const isLegacyStringValue = (value: FieldMappingSelectMultiValue): value is SelectOption =>
  typeof (value as unknown) === 'string';

const toChips = (
  entries: (SelectOption | string)[],
  options: SelectOption[],
): FieldMappingEditorValue | undefined => {
  if (entries.length === 0) return undefined;
  const items: ExtendedFieldMappingEditorValue[] = entries.map((entry) => ({
    value: toSelectedValue(entry),
  }));
  return transformSimpleValueInput(items, (rawValue) =>
    getSelectPlaceholderHtml({
      value: rawValue,
      label: (options.find((option) => option.value === rawValue)?.label as string) ?? rawValue,
    }),
  );
};

export const toEditorInput = (
  value: FieldMappingSelectMultiValue | undefined,
  options: SelectOption[],
): FieldMappingEditorValue | undefined => {
  if (!value) return undefined;
  if (Array.isArray(value)) return toChips(value, options);
  if (isLegacyStringValue(value) || isLegacySingleOption(value)) return toChips([value], options);
  return transformInput(value);
};

const parseEditorOutput = (
  value: FieldMappingEditorValue,
  options: SelectOption[],
): FieldMappingSelectMultiValue => {
  const hasVariables = value.variables && Object.keys(value.variables).length > 0;
  if (hasVariables) return value;
  return transformSimpleValueOutput(value, selectPlaceholderTag).map((item) => {
    const matched = options.find((option) => option.value === item.value);
    return {
      value: item.value,
      label: typeof matched?.label === 'string' ? matched.label : item.value,
    };
  });
};

// Same legacy shapes as toEditorInput: without this the manual picker shows
// nothing checked while the editor renders a chip for the stored selection.
export const extractSelectedValues = (
  value: FieldMappingSelectMultiValue | undefined,
): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(toSelectedValue);
  if (isLegacyStringValue(value) || isLegacySingleOption(value)) return [toSelectedValue(value)];
  return [];
};

export const FieldMappingSelectMulti = ({
  defaultValue,
  onChange,
  options,
  variableGroups,
  fieldMappingId,
  label,
  ...rest
}: FieldMappingSelectMultiProps) => {
  const { t } = useScopedTranslate(locale);
  const editorRef = useRef<EditorMethods>(null);
  const editorUuid = useRef(uuid()).current;
  const { isExtended, extendedId } = useExtendedSidebarData();
  const isThisOneExtended = isExtended && extendedId === editorUuid;

  const [currentValue, setCurrentValue] = useState<FieldMappingSelectMultiValue | undefined>(
    defaultValue,
  );
  const selectedValues = extractSelectedValues(currentValue);
  const selectedKey = selectedValues.join('|');

  const customShowFieldMappingSidebar = (props?: { variable?: FieldMappingFieldItem }) => {
    showExtendedSidebar({
      component: FieldMappingSidebar,
      props: {
        editorRef,
        variableGroups: variableGroups ?? [],
        fieldType: 'multi_select',
        label,
        fieldMappingId: fieldMappingId ?? '',
        manualData: options,
        selectedValues,
        onManualMultiSelect: (option: SelectOption) => {
          editorRef.current?.insertContent(
            getSelectPlaceholderHtml({
              value: option.value,
              label: option.label as string,
            }),
          );
        },
        ...props,
      },
      extendedId: editorUuid,
    });
  };

  useEffect(() => {
    if (isThisOneExtended) {
      customShowFieldMappingSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, isThisOneExtended]);

  return (
    <NodeStyledFieldMappingTextEditor
      ref={editorRef}
      extensions={[SelectPlaceholder, SingleVariableOrChips]}
      plaintext
      singleLine
      onChange={(value) => {
        const next = parseEditorOutput(value, options);
        setCurrentValue(next);
        onChange?.(next);
      }}
      defaultValue={toEditorInput(defaultValue, options)}
      placeholder={t('placeholder')}
      disabled
      shouldShowFieldMappingOnFocus
      fieldType="multi_select"
      manualData={options}
      label={label}
      variableGroups={variableGroups}
      fieldMappingId={fieldMappingId ?? ''}
      customShowFieldMappingSidebar={customShowFieldMappingSidebar}
      {...rest}
    />
  );
};

const locale = {
  en: {
    placeholder: 'Select values or add a variable',
  },
};
